import React, { useState, useEffect, useMemo } from 'react';
import { getFirestore, collection, addDoc, onSnapshot, serverTimestamp, query, doc, updateDoc, orderBy, increment, writeBatch } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import app from './firebase/firebaseConfig';
import { useAuth } from './context/AuthContext';

// Import all page and modal components
import Dashboard from './components/dashboard/Dashboard';
import LivestockDashboard from './components/livestock/LivestockDashboard';
import LivestockProfile from './components/livestock/LivestockProfile';
import FeedDashboard from './components/feed/FeedDashboard';
import MilkDashboard from './components/milk/MilkDashboard';
import FinanceDashboard from './components/finance/FinanceDashboard';
import BreedingDashboard from './components/breeding/BreedingDashboard';
import FeedDetailView from './components/feed/FeedDetailView';
import LivestockFormModal from './components/livestock/LivestockFormModal';
import LivestockTypeModal from './components/livestock/LivestockTypeModal';
import BreedingEventModal from './components/breeding/BreedingEventModal';
import UpdateBreedingStatusModal from './components/breeding/UpdateBreedingStatusModal';
import UpdateLivestockStatusModal from './components/livestock/UpdateLivestockStatusModal';
import AddFeedPurchaseModal from './components/feed/AddFeedPurchaseModal';
import AdminDashboard from './components/admin/AdminDashboard'; // Import the new AdminDashboard

const db = getFirestore(app);
const storage = getStorage(app);
const __app_id = app.options.appId;

const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function FarmManager() {
    const { currentUser, logout } = useAuth();
    const farmId = currentUser.farmId;
    const userRole = currentUser.role; // Get the user's role
    // App Data State
    const [livestock, setLivestock] = useState([]);
    const [livestockTypes, setLivestockTypes] = useState([]);
    const [feedInventory, setFeedInventory] = useState([]);
    const [breedingEvents, setBreedingEvents] = useState([]);
    const [feedPurchaseHistory, setFeedPurchaseHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // View Management State
    const [activeTab, setActiveTab] = useState('dashboard');
    const [currentView, setCurrentView] = useState('list');
    const [selectedLivestockId, setSelectedLivestockId] = useState(null);
    const [selectedFeedItemId, setSelectedFeedItemId] = useState(null);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingLivestock, setEditingLivestock] = useState(null);
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [isBreedingModalOpen, setIsBreedingModalOpen] = useState(false);
    const [editingBreedingEvent, setEditingBreedingEvent] = useState(null);
    const [editingStatusAnimal, setEditingStatusAnimal] = useState(null);
    const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
    const [editingPurchaseItem, setEditingPurchaseItem] = useState(null);

    // Interactivity State
    const [livestockSearchTerm, setLivestockSearchTerm] = useState('');
    const [livestockFilterType, setLivestockFilterType] = useState('All');
    const [livestockFilterStatus, setLivestockFilterStatus] = useState('All');
    const [livestockSortConfig, setLivestockSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [feedSearchTerm, setFeedSearchTerm] = useState('');
    const [feedFilterStatus, setFeedFilterStatus] = useState('All');
    const [feedSortConfig, setFeedSortConfig] = useState({ key: 'name', direction: 'ascending' });

    // Data Fetching
    useEffect(() => {
        if (farmId) {
            setIsLoading(true);
            const getPath = (subPath) => `/artifacts/${__app_id}/farms/${farmId}/${subPath}`;
            
            const unsubLivestock = onSnapshot(query(collection(db, getPath('livestock')), orderBy('createdAt', 'desc')), (snap) => {
                setLivestock(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setIsLoading(false);
            });
            const unsubFeed = onSnapshot(query(collection(db, getPath('feed_inventory')), orderBy('name')), (snap) => setFeedInventory(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
            const unsubTypes = onSnapshot(query(collection(db, getPath('livestock_types')), orderBy('name')), (snap) => setLivestockTypes(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
            const unsubBreeding = onSnapshot(query(collection(db, getPath('breeding_events')), orderBy('eventDate', 'desc')), (snap) => setBreedingEvents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
            const unsubPurchases = onSnapshot(query(collection(db, getPath('feed_purchases')), orderBy('date', 'desc')), (snap) => setFeedPurchaseHistory(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));

            return () => { unsubLivestock(); unsubFeed(); unsubTypes(); unsubBreeding(); unsubPurchases(); };
        }
    }, [farmId]);

    // Automatic Breeding Status Update Logic
    useEffect(() => {
        if (!farmId || breedingEvents.length === 0) return;
        const checkDueDates = async () => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const eventsToUpdate = breedingEvents.filter(event => {
                const dueDate = new Date(event.dueDate);
                dueDate.setUTCHours(0, 0, 0, 0);
                return event.status === 'In-Progress' && dueDate <= today;
            });
            if (eventsToUpdate.length > 0) {
                const batch = writeBatch(db);
                eventsToUpdate.forEach(event => {
                    const eventRef = doc(db, `/artifacts/${__app_id}/farms/${farmId}/breeding_events`, event.id);
                    batch.update(eventRef, { status: 'Due' });
                });
                await batch.commit().catch(e => console.error("Error auto-updating due dates:", e));
            }
        };
        const interval = setInterval(checkDueDates, 3600000);
        checkDueDates();
        return () => clearInterval(interval);
    }, [breedingEvents, farmId]);

    // --- Logic for Livestock Page ---
    const processedLivestock = useMemo(() => {
        let items = [...livestock];
        if (livestockFilterType !== 'All') items = items.filter(animal => animal.type === livestockFilterType);
        if (livestockFilterStatus !== 'All') items = items.filter(animal => animal.status === livestockFilterStatus);
        if (livestockSearchTerm) items = items.filter(animal => animal.name.toLowerCase().includes(livestockSearchTerm.toLowerCase()) || (animal.tagNumber && animal.tagNumber.toLowerCase().includes(livestockSearchTerm.toLowerCase())));
        if (livestockSortConfig.key) {
            items.sort((a, b) => {
                let aValue = a[livestockSortConfig.key];
                let bValue = b[livestockSortConfig.key];
                if (livestockSortConfig.key === 'dateOfBirth') {
                    aValue = new Date(a.dateOfBirth).getTime();
                    bValue = new Date(b.dateOfBirth).getTime();
                }
                if (aValue < bValue) return livestockSortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return livestockSortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [livestock, livestockSearchTerm, livestockFilterType, livestockFilterStatus, livestockSortConfig]);

    // --- Logic for Feed Page ---
    const processedFeedInventory = useMemo(() => {
        let items = [...feedInventory];
        if (feedFilterStatus === 'Low Stock') items = items.filter(item => item.stock <= item.lowStockThreshold);
        if (feedSearchTerm) items = items.filter(item => item.name.toLowerCase().includes(feedSearchTerm.toLowerCase()));
        if (feedSortConfig.key) {
            items.sort((a, b) => {
                const aValue = a[feedSortConfig.key];
                const bValue = b[feedSortConfig.key];
                if (aValue < bValue) return feedSortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return feedSortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [feedInventory, feedSearchTerm, feedFilterStatus, feedSortConfig]);

    // --- Handlers ---
    const requestLivestockSort = (key) => {
        let direction = 'ascending';
        if (livestockSortConfig.key === key && livestockSortConfig.direction === 'ascending') direction = 'descending';
        setLivestockSortConfig({ key, direction });
    };

    const requestFeedSort = (key) => {
        let direction = 'ascending';
        if (feedSortConfig.key === key && feedSortConfig.direction === 'ascending') direction = 'descending';
        setFeedSortConfig({ key, direction });
    };

    const handleAddLivestock = async (data) => {
        if (!farmId) return;
        setIsSubmitting(true);
        const path = `/artifacts/${__app_id}/farms/${farmId}/livestock`;
        
        try {
            if (editingLivestock) {
                await updateDoc(doc(db, path, editingLivestock.id), data);
            } else {
                await addDoc(collection(db, path), { ...data, farmId, createdAt: serverTimestamp() });
            }
            setIsFormModalOpen(false); setEditingLivestock(null);
        } catch (e) { console.error("Error writing document:", e); } finally { setIsSubmitting(false); }
    };
    
    const handleAddLivestockType = async (typeName) => {
        if (!farmId) return;
        const path = `/artifacts/${__app_id}/farms/${farmId}/livestock_types`;
        await addDoc(collection(db, path), { name: typeName, farmId });
    };

    const handleAddFeed = async (feedData) => {
        if(!farmId) return;
        setIsSubmitting(true);
        const inventoryPath = `/artifacts/${__app_id}/farms/${farmId}/feed_inventory`;
        const purchasePath = `/artifacts/${__app_id}/farms/${farmId}/feed_purchases`;
        const quantity = Number(feedData.quantity);
        const totalCost = Number(feedData.totalCost);
        const unitPrice = quantity > 0 ? totalCost / quantity : 0;

        try {
            const batch = writeBatch(db);
            const newInventoryRef = doc(collection(db, inventoryPath));
            batch.set(newInventoryRef, {
                name: feedData.name,
                stock: quantity,
                unitPrice: unitPrice,
                protein: Number(feedData.protein) || 0,
                fat: Number(feedData.fat) || 0,
                lowStockThreshold: Number(feedData.lowStockThreshold),
                farmId,
                createdAt: serverTimestamp()
            });
            const newPurchaseRef = doc(collection(db, purchasePath));
            batch.set(newPurchaseRef, {
                feedItemId: newInventoryRef.id,
                feedItemName: feedData.name,
                date: getLocalDateString(feedData.purchaseDate),
                quantity,
                totalCost,
                unitPrice,
                farmId,
                createdAt: serverTimestamp()
            });
            await batch.commit();
        } catch (e) { console.error("Error adding new feed item:", e); } finally { setIsSubmitting(false); }
    };

    const handleAddFeedPurchase = async (feedItem, purchaseData) => {
        if (!farmId) return;
        setIsSubmitting(true);
        const purchasePath = `/artifacts/${__app_id}/farms/${farmId}/feed_purchases`;
        const inventoryRef = doc(db, `/artifacts/${__app_id}/farms/${farmId}/feed_inventory`, feedItem.id);
        const quantity = Number(purchaseData.quantity);
        const totalCost = Number(purchaseData.totalCost);
        const pricePerKg = quantity > 0 ? totalCost / quantity : 0;

        try {
            const batch = writeBatch(db);
            batch.set(doc(collection(db, purchasePath)), {
                feedItemId: feedItem.id,
                feedItemName: feedItem.name,
                date: getLocalDateString(purchaseData.date),
                quantity,
                totalCost,
                unitPrice: pricePerKg,
                farmId,
                createdAt: serverTimestamp()
            });
            batch.update(inventoryRef, { 
                stock: increment(quantity),
                unitPrice: pricePerKg
            });
            await batch.commit();
        } catch(e) {
            console.error("Error recording purchase:", e);
        } finally {
            setIsSubmitting(false);
            setIsPurchaseModalOpen(false);
        }
    };

    const handleSaveRecipe = async (feedItemId, recipe) => {
        if (!farmId) return;
        const docRef = doc(db, `/artifacts/${__app_id}/farms/${farmId}/feed_inventory`, feedItemId);
        await updateDoc(docRef, { recipe });
    };

    const handleAddBreedingEvent = async (data) => {
        if (!farmId) return;
        setIsSubmitting(true);
        const path = `/artifacts/${__app_id}/farms/${farmId}/breeding_events`;
        try {
            const animalName = livestock.find(a => a.id === data.animalId)?.name || 'Unknown';
            await addDoc(collection(db, path), { ...data, animalName, farmId, createdAt: serverTimestamp() });
            setIsBreedingModalOpen(false);
        } catch (e) {
            console.error("Error adding breeding event:", e);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateBreedingStatus = async (eventId, newStatus, additionalData = {}) => {
        if (!farmId) return;
        setIsSubmitting(true);
        const batch = writeBatch(db);
        const breedingEventPath = `/artifacts/${__app_id}/farms/${farmId}/breeding_events`;
        const eventRef = doc(db, breedingEventPath, eventId);
        try {
            batch.update(eventRef, { status: newStatus });

            if (newStatus === 'Completed (Calved)' && additionalData.calvedDate) {
                const event = breedingEvents.find(e => e.id === eventId);
                console.log(event)
                const mother = livestock.find(a => a.id === event.animalId);

                if (!event || !mother) {
                    console.error("Could not find parent information for the new calf.");
                    throw new Error("Parent information missing.");
                }
                
                const livestockPath = `/artifacts/${__app_id}/farms/${farmId}/livestock`;
                const newCalfRef = doc(collection(db, livestockPath));

                const newCalfData = {
                    name: `Calf of ${mother.name}`,
                    dateOfBirth: additionalData.calvedDate,
                    type: mother.type,
                    status: 'Active',
                    gender: 'Unknown',
                    mother: mother.name ||mother.id ,
                    father: event.sire || 'Unknown',
                    farmId,
                    breedingMethod:event.eventType,
                    createdAt: serverTimestamp(),
                };
                
                batch.set(newCalfRef, newCalfData);
            }
            
            await batch.commit();
            setEditingBreedingEvent(null); 

        } catch (e) {
            console.error("Error in breeding status update process:", e);
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleUpdateLivestockStatus = async (animalId, newStatus) => {
        if (!farmId) return;
        setIsSubmitting(true);
        const path = `/artifacts/${__app_id}/farms/${farmId}/livestock`;
        try {
            const animalRef = doc(db, path, animalId);
            await updateDoc(animalRef, { status: newStatus });
            setEditingStatusAnimal(null);
        } catch (e) {
            console.error("Error updating livestock status:", e);
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectedLivestock = livestock.find(c => c.id === selectedLivestockId);
    const selectedFeedItem = feedInventory.find(f => f.id === selectedFeedItemId);
    const NavLink = ({ tabName, children }) => ( <button onClick={() => { setActiveTab(tabName); setCurrentView('list'); }} className={`py-2 px-4 text-sm font-medium rounded-md ${activeTab === tabName ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>{children}</button> );

    const renderContent = () => {
        const commonProps = { db, storage, farmId, feedInventory };
        const dashboardProps = { ...commonProps, livestock, userRole };

        if (userRole === 'worker' && (activeTab === 'dashboard' || activeTab === 'breeding' || activeTab === 'finance' || activeTab === 'admin')) {
            setActiveTab('livestock');
        }

        switch (activeTab) {
            case 'dashboard':
                return userRole !== 'worker' ? <Dashboard {...dashboardProps} /> : null;
            case 'livestock':
                return currentView === 'list' 
                    ? <LivestockDashboard 
                        livestock={processedLivestock} 
                        isLoading={isLoading} 
                        onSelectLivestock={(id) => { setSelectedLivestockId(id); setCurrentView('profile'); }} 
                        onAddLivestockClick={() => { setEditingLivestock(null); setIsFormModalOpen(true); }} 
                        onManageTypesClick={() => setIsTypeModalOpen(true)}
                        searchTerm={livestockSearchTerm}
                        setSearchTerm={setLivestockSearchTerm}
                        filterType={livestockFilterType}
                        setFilterType={setLivestockFilterType}
                        livestockTypes={livestockTypes}
                        requestSort={requestLivestockSort}
                        sortConfig={livestockSortConfig}
                        filterStatus={livestockFilterStatus}
                        setFilterStatus={setLivestockFilterStatus}
                        onUpdateStatusClick={(animal) => setEditingStatusAnimal(animal)}
                        userRole={userRole}
                      />
                    : <LivestockProfile 
                        animal={selectedLivestock} 
                        onBack={() => setCurrentView('list')} 
                        onEdit={(animal) => { setEditingLivestock(animal); setIsFormModalOpen(true); }} 
                        {...commonProps}
                        userRole={userRole}
                      />;
            case 'feed':
                 if (currentView === 'feedDetail' && selectedFeedItem) {
                    return <FeedDetailView 
                                feedItem={selectedFeedItem}
                                purchaseHistory={feedPurchaseHistory.filter(p => p.feedItemId === selectedFeedItem.id)}
                                onBack={() => { setCurrentView('list'); setSelectedFeedItemId(null); }}
                                onRecordPurchaseClick={(item) => { setEditingPurchaseItem(item); setIsPurchaseModalOpen(true); }}
                                {...commonProps}
                           />;
                }
                return <FeedDashboard 
                        feedInventory={processedFeedInventory} 
                        onAddFeed={handleAddFeed} 
                        isLoading={isLoading} 
                        searchTerm={feedSearchTerm}
                        setSearchTerm={setFeedSearchTerm}
                        filterStatus={feedFilterStatus}
                        setFilterStatus={setFeedFilterStatus}
                        requestSort={requestFeedSort}
                        sortConfig={feedSortConfig}
                        onSelectFeedItem={(item) => { setSelectedFeedItemId(item.id); setCurrentView('feedDetail'); }}
                        userRole={userRole}
                       />;
            case 'breeding':
                return userRole !== 'worker' ? <BreedingDashboard breedingEvents={breedingEvents} onAddEventClick={() => setIsBreedingModalOpen(true)} onUpdateStatusClick={(event) => setEditingBreedingEvent(event)} /> : null;
            case 'milk':
                 return userRole !== 'worker' ? <MilkDashboard {...commonProps} /> : null;
            case 'finance':
                return userRole !== 'worker' ? <FinanceDashboard {...commonProps} /> : null;
            case 'admin':
                return userRole === 'admin' ? <AdminDashboard /> : null;
            default:
                return userRole !== 'worker' ? <Dashboard {...dashboardProps} /> : null;
        }
    };

    return (
        <div className="bg-gray-100 min-h-screen">
            <header className="bg-white shadow-sm sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-3">
                         <h1 className="text-xl font-bold text-gray-900">Farm Manager</h1>
                         <nav className="flex space-x-2">
                            {userRole !== 'worker' && <NavLink tabName="dashboard">Dashboard</NavLink>}
                            <NavLink tabName="livestock">Livestock</NavLink>
                            {userRole !== 'worker' && <NavLink tabName="breeding">Breeding</NavLink>}
                            <NavLink tabName="feed">Feed</NavLink>
                            {userRole !== 'worker' && <NavLink tabName="milk">Milk</NavLink>}
                            {userRole !== 'worker' && <NavLink tabName="finance">Finance</NavLink>}
                            {userRole === 'admin' && <NavLink tabName="admin">Admin</NavLink>}
                         </nav>
                         <button onClick={logout} className="text-sm font-medium text-red-600 hover:text-red-800">Log Out</button>
                    </div>
                </div>
            </header>
            <main>
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="px-4 py-6 sm:px-0">{renderContent()}</div>
                </div>
            </main>
            <LivestockFormModal isOpen={isFormModalOpen} onClose={() => setIsFormModalOpen(false)} onSubmit={handleAddLivestock} isLoading={isSubmitting} initialData={editingLivestock} livestockTypes={livestockTypes} />
            <LivestockTypeModal isOpen={isTypeModalOpen} onClose={() => setIsTypeModalOpen(false)} onAddType={handleAddLivestockType} types={livestockTypes} />
            <BreedingEventModal 
                isOpen={isBreedingModalOpen} 
                onClose={() => setIsBreedingModalOpen(false)} 
                onSubmit={handleAddBreedingEvent} 
                isLoading={isSubmitting}
                livestock={livestock}
            />
            <UpdateBreedingStatusModal
                isOpen={!!editingBreedingEvent}
                onClose={() => setEditingBreedingEvent(null)}
                onSubmit={handleUpdateBreedingStatus}
                isLoading={isSubmitting}
                event={editingBreedingEvent}
            />
            <UpdateLivestockStatusModal
                isOpen={!!editingStatusAnimal}
                onClose={() => setEditingStatusAnimal(null)}
                onSubmit={handleUpdateLivestockStatus}
                isLoading={isSubmitting}
                animal={editingStatusAnimal}
            />
            <AddFeedPurchaseModal
                isOpen={isPurchaseModalOpen}
                onClose={() => setIsPurchaseModalOpen(false)}
                onSubmit={handleAddFeedPurchase}
                isLoading={isSubmitting}
                feedItem={editingPurchaseItem}
            />
        </div>
    );
}